import { useTranslation } from "@sagot/i18n";
import { ListEditor, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { normalizeDomain } from "./security-model";
import { WIDGET_LIMITS } from "./widget-model";

const K = "deployKnowledge.widgetStudio.security";

interface AllowedDomainsProps {
  domains: readonly string[];
  onChange: (domains: string[]) => void;
  disabled: boolean;
}

/** Widget allowed domains (`host`, `host:port`, `*.host`; localhost for local tests). Saved with the security form. */
export function AllowedDomains({ domains, onChange, disabled }: AllowedDomainsProps) {
  const { t } = useTranslation();
  const labels: ListEditorLabels = {
    add: t(`${K}.add`),
    placeholder: t(`${K}.domainPlaceholder`),
    empty: t(`${K}.noDomains`),
    count: (count, max) => t(`${K}.domainsCount`, { count, max }),
    remove: (domain) => t(`${K}.remove`, { domain }),
    duplicate: (domain) => t(`${K}.domainDuplicate`, { domain }),
    full: t(`${K}.domainsFull`, { max: WIDGET_LIMITS.maxDomains }),
    added: (domain) => t(`${K}.domainAdded`, { domain }),
    removed: (domain) => t(`${K}.domainRemoved`, { domain }),
  };
  const parse = (raw: string): ListEntryResult => {
    const value = normalizeDomain(raw);
    return value ? { ok: true, value } : { ok: false, error: t(`${K}.domainInvalid`) };
  };
  return (
    <ListEditor
      label={t(`${K}.allowedDomains`)}
      hint={t(`${K}.allowedDomainsHint`)}
      note={disabled ? undefined : t(`${K}.localHint`)}
      items={domains}
      onChange={onChange}
      parse={parse}
      max={WIDGET_LIMITS.maxDomains}
      labels={labels}
      icon="globe"
      disabled={disabled}
    />
  );
}
